import type { HelpContent } from './types';

export const resourceDirectoryHelp: HelpContent = {
  pageTitle: 'Resource Directory',
  description: 'A searchable directory of all people and assets in your organisation, including contact details, skills, rates, working patterns, and project allocations.',

  functionality: {
    overview: 'The Resource Directory gives you a complete view of every person and asset in your organisation. Search, filter, and browse resources, then open any resource to see their full profile including skills, rates, working pattern, project allocations, and company access.',

    keyFeatures: [
      {
        label: 'Search and filter',
        detail: 'Quickly find resources by name, role, department, or status using the search bar and filter panel.',
      },
      {
        label: 'Grid and list views',
        detail: 'Switch between a card grid view and a list view to suit how you prefer to browse your resources.',
      },
      {
        label: 'Column chooser',
        detail: 'In list view, choose which columns are visible using the column configurator to tailor the information shown.',
      },
      {
        label: 'Resource detail panel',
        detail: 'Click any resource to open a detailed side panel showing their profile, skills, rates, working pattern, project allocations, and company access.',
      },
      {
        label: 'Skills overview',
        detail: 'View the skills and proficiency levels recorded against each resource.',
      },
      {
        label: 'Project allocations',
        detail: 'See which projects a resource is currently allocated to, along with their role and allocation percentage.',
      },
      {
        label: 'Company access',
        detail: 'Manage which companies a resource has access to, and set their default company.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'Search bar',
        description: 'Filter resources in real time by typing a name, role, or department.',
      },
      {
        name: 'Filter panel',
        description: 'A side panel of filter controls. Filter resources by status, type, department, and other fields.',
      },
      {
        name: 'View toggle',
        description: 'Switch between the card grid view and the list view.',
      },
      {
        name: 'Column chooser',
        description: 'Available in list view. Choose which columns are displayed to tailor the information shown to your needs.',
      },
      {
        name: 'Resource card / row',
        description: 'Displays key information for each resource including name, role, department, email, and status. Click to open the full detail panel.',
      },
      {
        name: 'Resource detail panel — Profile',
        description: 'Shows the resource\'s contact information, role, department, grade, type, and their Access Projects platform role.',
      },
      {
        name: 'Resource detail panel — Skills tab',
        description: 'Lists the skills recorded against the resource, including category and proficiency rating.',
      },
      {
        name: 'Resource detail panel — Rates tab',
        description: 'Displays the resource\'s bill rate and cost rate, shown as both day and hour rates.',
      },
      {
        name: 'Resource detail panel — Working Pattern tab',
        description: 'Shows the resource\'s working pattern including working days, hours per day, total hours per week, and FTE.',
      },
      {
        name: 'Resource detail panel — Projects tab',
        description: 'Lists the projects the resource is currently allocated to, including their role, allocation percentage, and project dates.',
      },
      {
        name: 'Resource detail panel — Company Access tab',
        description: 'Shows all companies and allows you to toggle access for the resource and set their default company. Changes must be saved using the Save Changes button.',
      },
    ],

    useCases: [
      'Finding contact details for a colleague.',
      'Checking the skills and proficiency levels of a resource before allocating them to a project.',
      'Reviewing a resource\'s current project allocations and availability.',
      'Managing company access for a resource.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Resource Directory lists all people and assets in your organisation. Use the search bar or filter panel to find a specific resource, then click their card or row to view their full profile.',
    steps: [
      {
        title: 'How do I find a resource?',
        items: [
          { text: 'Use the search bar at the top to filter by name, role, or department.' },
          { text: 'Use the filter panel to narrow results by status, type, or other fields.' },
          { text: 'Switch between grid and list view using the view toggle.' },
        ],
      },
      {
        title: 'How do I view a resource\'s full profile?',
        items: [
          { text: 'Click any resource card or row to open the detail panel.' },
          { text: 'Use the tabs within the panel to navigate between Skills, Rates, Working Pattern, Projects, and Company Access.' },
        ],
      },
      {
        title: 'How do I manage company access for a resource?',
        items: [
          { text: 'Open the detail panel for a resource and go to the Company Access tab.' },
          { text: 'Toggle access on or off for each company.' },
          { text: 'Click "Set as default" to set a resource\'s default company.' },
          { text: 'Click Save Changes to apply your updates.' },
        ],
      },
      {
        title: 'How do I customise the columns in list view?',
        items: [
          { text: 'Switch to list view using the view toggle.' },
          { text: 'Click the column chooser icon to open the column configurator.' },
          { text: 'Select or deselect columns to tailor the information shown.' },
        ],
      },
    ],
    proTips: [
      'Switch to list view and use the column chooser to add the FTE and Hours/Week columns — useful for quickly checking working patterns across your team.',
      'The Projects tab in the resource detail panel shows current allocations at a glance — helpful when assessing availability before assigning someone to a new project.',
      'Use the filter panel to filter by status to quickly see only Active resources.',
    ],
  },

  faq: [
    {
      question: 'Can I edit a resource\'s details from the directory?',
      answer: 'Resource profile information such as name, role, and department is managed centrally and cannot be edited directly from the directory. Company access can be managed from the Company Access tab in the detail panel.',
    },
    {
      question: 'Why are some resources showing as Inactive?',
      answer: 'A resource is shown as Inactive when they are marked as unavailable in the system. Contact your Administrator if you believe this is incorrect.',
    },
    {
      question: 'Can I see a resource\'s bill and cost rates?',
      answer: 'Yes. Open the detail panel for a resource and go to the Rates tab to view their bill and cost rates. Visibility of rates may depend on your access permissions.',
    },
    {
      question: 'How do I find resources with a specific skill?',
      answer: 'Use the Skills page to browse and filter resources by skill and proficiency level. You can also open a resource\'s detail panel and check their Skills tab.',
    },
    {
      question: 'What is the Company Access tab used for?',
      answer: 'Company Access controls which companies a resource can see and work within in Access Projects. You can also set a default company for the resource from this tab.',
    },
  ],

  relatedPages: [
    { label: 'Roles & Rates', path: '/roles-rates' },
    { label: 'Skills', path: '/skills' },
  ],
};
